import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { adminAPI, getAssetUrl, BASE_URL } from "../../lib/api";
import { formatNumber } from "../../lib/utils";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Progress } from "../ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import {
  Users,
  Film,
  CreditCard,
  TrendingUp,
  Shield,
  ShieldCheck,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Clock,
  Activity,
  Eye,
  Trash2,
  UserCheck,
  UserX,
  Crown,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Gift,
  DollarSign,
  Wallet,
  BarChart3,
  History,
  MessageSquare,
  FileText,
  Send,
  Key,
  Ban,
  PlayCircle,
  Download,
  ExternalLink,
  Loader2,
  StickyNote,
  LogIn,
  Monitor,
  Globe,
  Smartphone,
  ChevronRight,
  Plus,
  Star,
  Zap,
} from "lucide-react";

// Format date helper
const formatDate = (dateStr) => {
  if (!dateStr) return "N/A";
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// Format currency helper
const formatCurrency = (amount) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount || 0);
};

// Health score calculation
const calculateHealthScore = (userData) => {
  let score = 0;
  
  // Has recent activity (within 7 days)
  if (userData.last_activity) {
    const daysSinceActivity = (Date.now() - new Date(userData.last_activity)) / (1000 * 60 * 60 * 24);
    if (daysSinceActivity < 7) score += 30;
    else if (daysSinceActivity < 14) score += 20;
    else if (daysSinceActivity < 30) score += 10;
  }
  
  // Has created videos
  if (userData.video_count > 0) score += 20;
  if (userData.video_count >= 5) score += 10;
  if (userData.video_count >= 10) score += 10;
  
  // Has subscription
  if (userData.subscription?.plan && userData.subscription.plan !== "free") score += 20;
  
  // Active subscription status
  if (userData.subscription?.status === "active") score += 10;
  
  return Math.min(score, 100);
};

// Health score badge
const HealthBadge = ({ score }) => {
  let color, label;
  if (score >= 80) { color = "bg-green-500/20 text-green-400 border-green-500/30"; label = "Healthy"; }
  else if (score >= 50) { color = "bg-yellow-500/20 text-yellow-400 border-yellow-500/30"; label = "At Risk"; }
  else { color = "bg-red-500/20 text-red-400 border-red-500/30"; label = "Churning"; }
  
  return (
    <Badge className={`${color} border`}>
      {label} ({score}%)
    </Badge>
  );
};

// Device icon helper
const getDeviceIcon = (device) => {
  if (!device) return <Monitor className="w-4 h-4" />;
  if (device.toLowerCase().includes("mobile") || device.toLowerCase().includes("iphone") || device.toLowerCase().includes("android")) {
    return <Smartphone className="w-4 h-4" />;
  }
  return <Monitor className="w-4 h-4" />;
};

export default function UserProfileModal({ 
  open, 
  onOpenChange, 
  userId,
  onUserUpdated,
  onViewVideos,
  onManageTrial,
}) {
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  
  // Extended data states
  const [usageData, setUsageData] = useState(null);
  const [activityLogs, setActivityLogs] = useState([]);
  const [userVideos, setUserVideos] = useState([]);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [adminNotes, setAdminNotes] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [trialHistory, setTrialHistory] = useState([]);
  const [subscriptionHistory, setSubscriptionHistory] = useState([]);
  
  // Action states
  const [actionLoading, setActionLoading] = useState(null);
  const [grantCreditsOpen, setGrantCreditsOpen] = useState(false);
  const [creditsToGrant, setCreditsToGrant] = useState(5);
  const [assignSubOpen, setAssignSubOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState("pro");
  const [selectedDuration, setSelectedDuration] = useState("monthly");

  // Fetch user data
  const fetchUserData = useCallback(async () => {
    if (!userId) return;
    
    setLoading(true);
    try {
      const response = await adminAPI.getUser(userId);
      setUserData(response.data);
      
      // Fetch additional data based on what's available
      try {
        const authToken = localStorage.getItem("auth_token") || localStorage.getItem("token");
        const [usageRes, videosRes, trialRes] = await Promise.all([
          adminAPI.getUserUsageStats?.(userId).catch(() => null),
          adminAPI.getUserVideos?.(response.data.email, 0, 10).catch(() => null),
          fetch(`${BASE_URL}/api/admin/trial/user/${encodeURIComponent(response.data.email)}`, {
            headers: { Authorization: `Bearer ${authToken}` },
          }).then(r => r.ok ? r.json() : null).catch(() => null),
        ]);
        
        if (usageRes?.data) setUsageData(usageRes.data);
        if (videosRes?.data) setUserVideos(videosRes.data.videos || []);
        if (trialRes?.trial_history) setTrialHistory(trialRes.trial_history || []);
        if (trialRes?.subscription_history) setSubscriptionHistory(trialRes.subscription_history || []);
      } catch (e) {
        console.log("Extended data not available");
      }
    } catch (error) {
      console.error("Error fetching user data:", error);
      toast.error("Failed to load user profile");
      onOpenChange(false);
    } finally {
      setLoading(false);
    }
  }, [userId, onOpenChange]);

  useEffect(() => {
    if (open && userId) {
      fetchUserData();
      setActiveTab("overview");
    }
  }, [open, userId, fetchUserData]);

  // Quick actions
  const handleToggleActive = async () => {
    setActionLoading("toggle");
    try {
      await adminAPI.updateUser(userId, { is_active: !userData.is_active });
      toast.success(userData.is_active ? "User disabled" : "User enabled");
      fetchUserData();
      onUserUpdated?.();
    } catch (error) {
      toast.error("Failed to update user status");
    } finally {
      setActionLoading(null);
    }
  };

  const handleMakeAdmin = async () => {
    setActionLoading("admin");
    try {
      await adminAPI.makeAdmin(userData.email);
      toast.success("User promoted to admin");
      fetchUserData();
      onUserUpdated?.();
    } catch (error) {
      toast.error("Failed to make user admin");
    } finally {
      setActionLoading(null);
    }
  };

  const handleSendPasswordReset = async () => {
    setActionLoading("reset");
    try {
      // This would call a password reset endpoint
      toast.success("Password reset email sent");
    } catch (error) {
      toast.error("Failed to send reset email");
    } finally {
      setActionLoading(null);
    }
  };

  const handleGrantCredits = async () => {
    if (!creditsToGrant || creditsToGrant <= 0) {
      toast.error("Please enter a valid credit amount");
      return;
    }
    
    setActionLoading("credits");
    try {
      const response = await adminAPI.grantCredits(userId, creditsToGrant, "Admin granted via User Profile");
      
      if (response?.data?.success) {
        toast.success(`Granted ${creditsToGrant} credits. New balance: ${response.data.new_balance}`);
        setGrantCreditsOpen(false);
        setCreditsToGrant(5); // Reset to default
        fetchUserData(); // Refresh user data
        onUserUpdated?.();
      } else {
        toast.error(response?.data?.message || "Failed to grant credits");
      }
    } catch (error) {
      console.error("Grant credits error:", error);
      const errorMsg = error.response?.data?.detail || error.message || "Failed to grant credits";
      toast.error(errorMsg);
    } finally {
      setActionLoading(null);
    }
  };

  const handleAssignSubscription = async () => {
    if (!userData?.email) {
      toast.error("User email not found");
      return;
    }
    
    setActionLoading("subscription");
    try {
      const response = await adminAPI.assignSubscription(userData.email, selectedPlan, selectedDuration);
      
      if (response?.data?.success) {
        toast.success(`Assigned ${selectedPlan} subscription successfully`);
        setAssignSubOpen(false);
        fetchUserData();
        onUserUpdated?.();
      } else {
        toast.error(response?.data?.error || response?.data?.detail || "Failed to assign subscription");
      }
    } catch (error) {
      console.error("Assign subscription error:", error);
      const errorMsg = error.response?.data?.detail || error.message || "Failed to assign subscription";
      toast.error(errorMsg);
    } finally {
      setActionLoading(null);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    setActionLoading("note");
    try {
      // This would call an add note endpoint
      setAdminNotes(prev => [...prev, {
        id: Date.now(),
        content: newNote,
        created_at: new Date().toISOString(),
        created_by: "Admin"
      }]);
      setNewNote("");
      toast.success("Note added");
    } catch (error) {
      toast.error("Failed to add note");
    } finally {
      setActionLoading(null);
    }
  };

  if (!open) return null;

  const healthScore = userData ? calculateHealthScore(userData) : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden bg-[#0A0A0B] border-border/50 p-0">
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-8 h-8 animate-spin text-[#00F0FF]" />
          </div>
        ) : userData ? (
          <div className="flex flex-col h-full max-h-[90vh]">
            {/* Header */}
            <div className="p-6 border-b border-border/50 bg-gradient-to-r from-[#0A0A0B] to-[#1a1a1b]">
              <div className="flex items-start gap-4">
                {/* Avatar */}
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#00F0FF] to-[#FF0055] flex items-center justify-center text-white font-bold text-3xl flex-shrink-0">
                  {userData.profile_image ? (
                    <img src={userData.profile_image} alt="" className="w-full h-full rounded-full object-cover" />
                  ) : (
                    userData.name?.charAt(0) || userData.email?.charAt(0) || "?"
                  )}
                </div>
                
                {/* User Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="text-2xl font-bold text-white truncate">{userData.name || "No name"}</h2>
                    {userData.is_admin && (
                      <Badge className="bg-[#FF0055]/20 text-[#FF0055] border-none">
                        <Crown className="w-3 h-3 mr-1" />
                        Admin
                      </Badge>
                    )}
                    {userData.is_active ? (
                      <Badge className="bg-green-500/20 text-green-400 border-none">Active</Badge>
                    ) : (
                      <Badge className="bg-red-500/20 text-red-400 border-none">Disabled</Badge>
                    )}
                    <HealthBadge score={healthScore} />
                  </div>
                  <p className="text-muted-foreground flex items-center gap-2 mt-1">
                    <Mail className="w-4 h-4" />
                    {userData.email}
                  </p>
                  <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      Joined {formatDate(userData.created_at)}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      Last seen {formatDate(userData.last_activity)}
                    </span>
                  </div>
                </div>

                {/* Quick Stats */}
                <div className="flex gap-3 flex-shrink-0">
                  <div className="text-center px-4 py-2 bg-secondary/30 rounded-lg">
                    <p className="text-2xl font-bold text-white">{userData.video_count || 0}</p>
                    <p className="text-xs text-muted-foreground">Videos</p>
                  </div>
                  <div className="text-center px-4 py-2 bg-secondary/30 rounded-lg">
                    <p className="text-2xl font-bold text-[#00F0FF]">
                      {userData.subscription?.videos_remaining ?? "∞"}
                    </p>
                    <p className="text-xs text-muted-foreground">Credits</p>
                  </div>
                  <div className="text-center px-4 py-2 bg-secondary/30 rounded-lg">
                    <p className="text-2xl font-bold text-purple-400 capitalize">
                      {userData.subscription?.plan || "Free"}
                    </p>
                    <p className="text-xs text-muted-foreground">Plan</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Tabs Navigation */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
              <div className="border-b border-border/50 px-6">
                <TabsList className="bg-transparent h-12 p-0 gap-0">
                  <TabsTrigger value="overview" className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-[#00F0FF] rounded-none px-4">
                    <BarChart3 className="w-4 h-4 mr-2" />
                    Overview
                  </TabsTrigger>
                  <TabsTrigger value="subscription" className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-[#00F0FF] rounded-none px-4">
                    <CreditCard className="w-4 h-4 mr-2" />
                    Subscription
                  </TabsTrigger>
                  <TabsTrigger value="usage" className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-[#00F0FF] rounded-none px-4">
                    <TrendingUp className="w-4 h-4 mr-2" />
                    Usage
                  </TabsTrigger>
                  <TabsTrigger value="videos" className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-[#00F0FF] rounded-none px-4">
                    <Film className="w-4 h-4 mr-2" />
                    Videos
                  </TabsTrigger>
                  <TabsTrigger value="activity" className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-[#00F0FF] rounded-none px-4">
                    <History className="w-4 h-4 mr-2" />
                    Activity
                  </TabsTrigger>
                  <TabsTrigger value="notes" className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-[#00F0FF] rounded-none px-4">
                    <StickyNote className="w-4 h-4 mr-2" />
                    Notes
                  </TabsTrigger>
                  <TabsTrigger value="actions" className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-[#00F0FF] rounded-none px-4">
                    <Zap className="w-4 h-4 mr-2" />
                    Actions
                  </TabsTrigger>
                </TabsList>
              </div>

              {/* Tab Contents */}
              <div className="flex-1 overflow-y-auto p-6">
                {/* Overview Tab */}
                <TabsContent value="overview" className="mt-0 space-y-6">
                  {/* Account Details Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card className="bg-secondary/30 border-border/50">
                      <CardContent className="p-4">
                        <p className="text-muted-foreground text-sm">Auth Provider</p>
                        <p className="text-white font-medium capitalize mt-1">{userData.auth_provider || "Email"}</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-secondary/30 border-border/50">
                      <CardContent className="p-4">
                        <p className="text-muted-foreground text-sm">User ID</p>
                        <p className="text-white font-mono text-xs mt-1 truncate">{userData.id}</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-secondary/30 border-border/50">
                      <CardContent className="p-4">
                        <p className="text-muted-foreground text-sm">Referral Code</p>
                        <p className="text-white font-medium mt-1">{userData.referral_code || "N/A"}</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-secondary/30 border-border/50">
                      <CardContent className="p-4">
                        <p className="text-muted-foreground text-sm">Referred By</p>
                        <p className="text-white font-medium mt-1">{userData.referred_by || "Direct"}</p>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Subscription Summary */}
                  {userData.subscription && (
                    <Card className="bg-gradient-to-r from-purple-500/10 to-cyan-500/10 border-purple-500/20">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg flex items-center gap-2">
                          <Crown className="w-5 h-5 text-purple-400" />
                          Subscription Overview
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div>
                            <p className="text-muted-foreground text-sm">Plan</p>
                            <p className="text-white font-semibold capitalize">{userData.subscription.plan || "Free"}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground text-sm">Status</p>
                            <Badge className={userData.subscription.status === "active" ? "bg-green-500/20 text-green-400" : "bg-yellow-500/20 text-yellow-400"}>
                              {userData.subscription.status || "Active"}
                            </Badge>
                          </div>
                          <div>
                            <p className="text-muted-foreground text-sm">Videos Used</p>
                            <p className="text-white font-semibold">
                              {userData.subscription.videos_used || 0} / {userData.subscription.video_limit || "∞"}
                            </p>
                          </div>
                          <div>
                            <p className="text-muted-foreground text-sm">Renewal</p>
                            <p className="text-white font-semibold">{formatDate(userData.subscription.renewal_date)}</p>
                          </div>
                        </div>
                        {userData.subscription.videos_used !== undefined && userData.subscription.video_limit && (
                          <div className="mt-4">
                            <Progress 
                              value={(userData.subscription.videos_used / userData.subscription.video_limit) * 100} 
                              className="h-2"
                            />
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  )}

                  {/* Quick Actions Row */}
                  <div className="flex gap-3 flex-wrap">
                    <Button
                      variant="outline"
                      className="border-white/20"
                      onClick={() => onViewVideos?.(userData.email)}
                    >
                      <Film className="w-4 h-4 mr-2" />
                      View All Videos
                    </Button>
                    <Button
                      variant="outline"
                      className="border-white/20"
                      onClick={() => onManageTrial?.(userData.email)}
                    >
                      <Calendar className="w-4 h-4 mr-2" />
                      Manage Trial
                    </Button>
                    <Button
                      variant="outline"
                      className="border-white/20"
                      onClick={() => setActiveTab("actions")}
                    >
                      <Zap className="w-4 h-4 mr-2" />
                      Quick Actions
                    </Button>
                  </div>
                </TabsContent>

                {/* Subscription Tab */}
                <TabsContent value="subscription" className="mt-0 space-y-6">
                  {/* Current Subscription */}
                  <Card className="bg-secondary/30 border-border/50">
                    <CardHeader>
                      <CardTitle className="text-lg">Current Subscription</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        <div>
                          <p className="text-muted-foreground text-sm">Plan</p>
                          <p className="text-white font-semibold capitalize text-lg">
                            {userData.subscription?.plan || "Free"}
                          </p>
                        </div>
                        <div>
                          <p className="text-muted-foreground text-sm">Status</p>
                          <Badge className={userData.subscription?.status === "active" ? "bg-green-500/20 text-green-400" : "bg-yellow-500/20 text-yellow-400"}>
                            {userData.subscription?.status || "Active"}
                          </Badge>
                        </div>
                        <div>
                          <p className="text-muted-foreground text-sm">Billing Cycle</p>
                          <p className="text-white font-medium capitalize">
                            {userData.subscription?.billing_cycle || "N/A"}
                          </p>
                        </div>
                        <div>
                          <p className="text-muted-foreground text-sm">Videos Remaining</p>
                          <p className="text-white font-semibold">
                            {userData.subscription?.videos_remaining ?? "Unlimited"}
                          </p>
                        </div>
                        <div>
                          <p className="text-muted-foreground text-sm">Next Renewal</p>
                          <p className="text-white font-medium">
                            {formatDate(userData.subscription?.renewal_date)}
                          </p>
                        </div>
                        <div>
                          <p className="text-muted-foreground text-sm">Trial Status</p>
                          <Badge className={userData.subscription?.has_trial_grant ? "bg-purple-500/20 text-purple-400" : "bg-gray-500/20 text-gray-400"}>
                            {userData.subscription?.has_trial_grant ? "Trial Active" : "No Trial"}
                          </Badge>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Payment History */}
                  <Card className="bg-secondary/30 border-border/50">
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <DollarSign className="w-5 h-5 text-green-400" />
                        Payment History
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {paymentHistory.length > 0 ? (
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Date</TableHead>
                              <TableHead>Amount</TableHead>
                              <TableHead>Plan</TableHead>
                              <TableHead>Status</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {paymentHistory.map((payment) => (
                              <TableRow key={payment.id}>
                                <TableCell>{formatDate(payment.date)}</TableCell>
                                <TableCell>{formatCurrency(payment.amount)}</TableCell>
                                <TableCell className="capitalize">{payment.plan}</TableCell>
                                <TableCell>
                                  <Badge className={payment.status === "succeeded" ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"}>
                                    {payment.status}
                                  </Badge>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      ) : (
                        <p className="text-muted-foreground text-center py-8">No payment history available</p>
                      )}
                    </CardContent>
                  </Card>

                  {/* Lifetime Value */}
                  <div className="grid grid-cols-3 gap-4">
                    <Card className="bg-secondary/30 border-border/50">
                      <CardContent className="p-4 text-center">
                        <DollarSign className="w-8 h-8 text-green-400 mx-auto mb-2" />
                        <p className="text-2xl font-bold text-white">{formatCurrency(userData.lifetime_value || 0)}</p>
                        <p className="text-muted-foreground text-sm">Lifetime Value</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-secondary/30 border-border/50">
                      <CardContent className="p-4 text-center">
                        <CreditCard className="w-8 h-8 text-blue-400 mx-auto mb-2" />
                        <p className="text-2xl font-bold text-white">{userData.total_payments || 0}</p>
                        <p className="text-muted-foreground text-sm">Total Payments</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-secondary/30 border-border/50">
                      <CardContent className="p-4 text-center">
                        <RefreshCw className="w-8 h-8 text-purple-400 mx-auto mb-2" />
                        <p className="text-2xl font-bold text-white">{userData.subscription_months || 0}</p>
                        <p className="text-muted-foreground text-sm">Months Subscribed</p>
                      </CardContent>
                    </Card>
                  </div>
                </TabsContent>

                {/* Usage Tab */}
                <TabsContent value="usage" className="mt-0 space-y-6">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card className="bg-secondary/30 border-border/50">
                      <CardContent className="p-4 text-center">
                        <Film className="w-8 h-8 text-[#00F0FF] mx-auto mb-2" />
                        <p className="text-2xl font-bold text-white">{userData.video_count || 0}</p>
                        <p className="text-muted-foreground text-sm">Total Videos</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-secondary/30 border-border/50">
                      <CardContent className="p-4 text-center">
                        <Zap className="w-8 h-8 text-yellow-400 mx-auto mb-2" />
                        <p className="text-2xl font-bold text-white">{usageData?.credits_used || 0}</p>
                        <p className="text-muted-foreground text-sm">Credits Used</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-secondary/30 border-border/50">
                      <CardContent className="p-4 text-center">
                        <Activity className="w-8 h-8 text-green-400 mx-auto mb-2" />
                        <p className="text-2xl font-bold text-white">{usageData?.api_calls || 0}</p>
                        <p className="text-muted-foreground text-sm">API Calls</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-secondary/30 border-border/50">
                      <CardContent className="p-4 text-center">
                        <Download className="w-8 h-8 text-purple-400 mx-auto mb-2" />
                        <p className="text-2xl font-bold text-white">{usageData?.storage_mb || 0} MB</p>
                        <p className="text-muted-foreground text-sm">Storage Used</p>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Usage by Type */}
                  <Card className="bg-secondary/30 border-border/50">
                    <CardHeader>
                      <CardTitle className="text-lg">Video Generation by Type</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div>
                          <div className="flex justify-between mb-1">
                            <span className="text-sm text-muted-foreground">UGC Videos</span>
                            <span className="text-sm text-white">{usageData?.ugc_videos || 0}</span>
                          </div>
                          <Progress value={(usageData?.ugc_videos || 0) / Math.max(userData.video_count || 1, 1) * 100} className="h-2" />
                        </div>
                        <div>
                          <div className="flex justify-between mb-1">
                            <span className="text-sm text-muted-foreground">Premium Videos</span>
                            <span className="text-sm text-white">{usageData?.premium_videos || 0}</span>
                          </div>
                          <Progress value={(usageData?.premium_videos || 0) / Math.max(userData.video_count || 1, 1) * 100} className="h-2" />
                        </div>
                        <div>
                          <div className="flex justify-between mb-1">
                            <span className="text-sm text-muted-foreground">Story Mode</span>
                            <span className="text-sm text-white">{usageData?.story_videos || 0}</span>
                          </div>
                          <Progress value={(usageData?.story_videos || 0) / Math.max(userData.video_count || 1, 1) * 100} className="h-2" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Feature Adoption */}
                  <Card className="bg-secondary/30 border-border/50">
                    <CardHeader>
                      <CardTitle className="text-lg">Feature Adoption</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {[
                          { name: "UGC Studio", used: usageData?.used_ugc },
                          { name: "Script Doctor", used: usageData?.used_script_doctor },
                          { name: "Voice Cloning", used: usageData?.used_voice_clone },
                          { name: "Brand Kit", used: usageData?.used_brand_kit },
                          { name: "Thumbnails", used: usageData?.used_thumbnails },
                          { name: "TikTok Post", used: usageData?.used_tiktok },
                          { name: "Templates", used: usageData?.used_templates },
                          { name: "Viral Predictor", used: usageData?.used_predictor },
                        ].map((feature) => (
                          <div key={feature.name} className={`p-3 rounded-lg border ${feature.used ? 'bg-green-500/10 border-green-500/30' : 'bg-secondary/50 border-border/50'}`}>
                            <div className="flex items-center gap-2">
                              {feature.used ? (
                                <CheckCircle className="w-4 h-4 text-green-400" />
                              ) : (
                                <XCircle className="w-4 h-4 text-muted-foreground" />
                              )}
                              <span className={`text-sm ${feature.used ? 'text-white' : 'text-muted-foreground'}`}>
                                {feature.name}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Videos Tab */}
                <TabsContent value="videos" className="mt-0 space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-white">Recent Videos</h3>
                    <Button variant="outline" className="border-white/20" onClick={() => onViewVideos?.(userData.email)}>
                      View All ({userData.video_count || 0})
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                  
                  {userVideos.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {userVideos.slice(0, 6).map((video) => (
                        <Card key={video.id || video._id} className="bg-secondary/30 border-border/50 overflow-hidden">
                          <div className="flex gap-3 p-3">
                            <div className="w-24 h-16 rounded bg-black flex-shrink-0 overflow-hidden">
                              {video.thumbnail_url ? (
                                <img src={getAssetUrl(video.thumbnail_url)} alt="" className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <Film className="w-6 h-6 text-muted-foreground" />
                                </div>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-white font-medium truncate">{video.title || "Untitled"}</p>
                              <p className="text-muted-foreground text-sm">{formatDate(video.created_at)}</p>
                              <Badge className="mt-1 capitalize" variant="outline">
                                {video.status || "pending"}
                              </Badge>
                            </div>
                          </div>
                        </Card>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <Film className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                      <p className="text-muted-foreground">No videos found</p>
                    </div>
                  )}
                </TabsContent>

                {/* Activity Tab */}
                <TabsContent value="activity" className="mt-0 space-y-4">
                  {/* Trial History */}
                  <Card className="bg-secondary/30 border-border/50">
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Gift className="w-5 h-5 text-purple-400" />
                        Trial History
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {trialHistory.length > 0 ? (
                        <div className="space-y-3">
                          {trialHistory.slice(0, 5).map((trial, idx) => (
                            <div key={trial.id || idx} className="flex items-center justify-between p-3 bg-secondary/50 rounded-lg">
                              <div>
                                <div className="flex items-center gap-2">
                                  <span className="text-white font-medium">{trial.video_limit} videos</span>
                                  <Badge className={
                                    trial.status === "active" ? "bg-green-500/20 text-green-400" :
                                    trial.status === "expired" ? "bg-yellow-500/20 text-yellow-400" :
                                    "bg-red-500/20 text-red-400"
                                  }>
                                    {trial.status}
                                  </Badge>
                                </div>
                                <p className="text-muted-foreground text-sm">
                                  {trial.reason || "No reason provided"}
                                </p>
                                <p className="text-muted-foreground text-xs">
                                  Granted: {formatDate(trial.granted_at)} → Expires: {formatDate(trial.expires_at)}
                                </p>
                              </div>
                              <div className="text-right text-sm text-muted-foreground">
                                {trial.granted_by_email || "System"}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-6">
                          <Gift className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
                          <p className="text-muted-foreground">No trial history</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Login Activity */}
                  <Card className="bg-secondary/30 border-border/50">
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <LogIn className="w-5 h-5 text-[#00F0FF]" />
                        Recent Login Activity
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {activityLogs.length > 0 ? (
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Date</TableHead>
                              <TableHead>Action</TableHead>
                              <TableHead>IP Address</TableHead>
                              <TableHead>Device</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {activityLogs.map((log) => (
                              <TableRow key={log.id}>
                                <TableCell>{formatDate(log.timestamp)}</TableCell>
                                <TableCell className="capitalize">{log.action}</TableCell>
                                <TableCell>{log.ip_address || "N/A"}</TableCell>
                                <TableCell className="flex items-center gap-2">
                                  {getDeviceIcon(log.device)}
                                  {log.device || "Unknown"}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      ) : (
                        <div className="text-center py-8">
                          <History className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                          <p className="text-muted-foreground">No activity logs available</p>
                          <p className="text-muted-foreground text-sm">Login history will appear here</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Notes Tab */}
                <TabsContent value="notes" className="mt-0 space-y-4">
                  <Card className="bg-secondary/30 border-border/50">
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <StickyNote className="w-5 h-5 text-yellow-400" />
                        Admin Notes
                      </CardTitle>
                      <CardDescription>Internal notes about this user (not visible to the user)</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* Add Note */}
                      <div className="flex gap-2">
                        <Textarea
                          value={newNote}
                          onChange={(e) => setNewNote(e.target.value)}
                          placeholder="Add a note about this user..."
                          className="bg-secondary/50 border-border/50 resize-none"
                          rows={2}
                        />
                        <Button
                          onClick={handleAddNote}
                          disabled={!newNote.trim() || actionLoading === "note"}
                          className="bg-[#00F0FF] hover:bg-[#00F0FF]/90 text-[#0A0A0B]"
                        >
                          {actionLoading === "note" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                        </Button>
                      </div>

                      {/* Notes List */}
                      <div className="space-y-3">
                        {adminNotes.length > 0 ? (
                          adminNotes.map((note) => (
                            <div key={note.id} className="p-3 bg-secondary/50 rounded-lg">
                              <p className="text-white">{note.content}</p>
                              <p className="text-muted-foreground text-xs mt-2">
                                {note.created_by} • {formatDate(note.created_at)}
                              </p>
                            </div>
                          ))
                        ) : (
                          <p className="text-muted-foreground text-center py-4">No notes yet</p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Actions Tab */}
                <TabsContent value="actions" className="mt-0 space-y-6">
                  {/* Account Actions */}
                  <Card className="bg-secondary/30 border-border/50">
                    <CardHeader>
                      <CardTitle className="text-lg">Account Actions</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <Button
                          variant="outline"
                          className="justify-start border-white/20"
                          onClick={handleSendPasswordReset}
                          disabled={actionLoading === "reset"}
                        >
                          {actionLoading === "reset" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Key className="w-4 h-4 mr-2" />}
                          Send Password Reset
                        </Button>
                        <Button
                          variant="outline"
                          className={`justify-start ${userData.is_active ? 'border-red-500/50 text-red-400' : 'border-green-500/50 text-green-400'}`}
                          onClick={handleToggleActive}
                          disabled={actionLoading === "toggle"}
                        >
                          {actionLoading === "toggle" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : userData.is_active ? <Ban className="w-4 h-4 mr-2" /> : <UserCheck className="w-4 h-4 mr-2" />}
                          {userData.is_active ? "Disable Account" : "Enable Account"}
                        </Button>
                        {!userData.is_admin && (
                          <Button
                            variant="outline"
                            className="justify-start border-purple-500/50 text-purple-400"
                            onClick={handleMakeAdmin}
                            disabled={actionLoading === "admin"}
                          >
                            {actionLoading === "admin" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Crown className="w-4 h-4 mr-2" />}
                            Promote to Admin
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Subscription Actions */}
                  <Card className="bg-secondary/30 border-border/50">
                    <CardHeader>
                      <CardTitle className="text-lg">Subscription Actions</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <Button
                          variant="outline"
                          className="justify-start border-[#00F0FF]/50 text-[#00F0FF]"
                          onClick={() => setGrantCreditsOpen(true)}
                        >
                          <Gift className="w-4 h-4 mr-2" />
                          Grant Credits
                        </Button>
                        <Button
                          variant="outline"
                          className="justify-start border-purple-500/50 text-purple-400"
                          onClick={() => setAssignSubOpen(true)}
                        >
                          <CreditCard className="w-4 h-4 mr-2" />
                          Assign Subscription
                        </Button>
                        <Button
                          variant="outline"
                          className="justify-start border-white/20"
                          onClick={() => onManageTrial?.(userData.email)}
                        >
                          <Calendar className="w-4 h-4 mr-2" />
                          Manage Trial
                        </Button>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Danger Zone */}
                  <Card className="bg-red-500/5 border-red-500/20">
                    <CardHeader>
                      <CardTitle className="text-lg text-red-400">Danger Zone</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Button
                        variant="outline"
                        className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete User Account
                      </Button>
                      <p className="text-muted-foreground text-xs mt-2">
                        This action cannot be undone. All user data will be permanently deleted.
                      </p>
                    </CardContent>
                  </Card>
                </TabsContent>
              </div>
            </Tabs>
          </div>
        ) : null}
      </DialogContent>

      {/* Grant Credits Dialog */}
      <Dialog open={grantCreditsOpen} onOpenChange={setGrantCreditsOpen}>
        <DialogContent className="bg-[#0A0A0B] border-border/50">
          <DialogHeader>
            <DialogTitle>Grant Credits</DialogTitle>
            <DialogDescription>Add video generation credits to this user's account</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Number of Credits</Label>
              <Input
                type="number"
                value={creditsToGrant}
                onChange={(e) => setCreditsToGrant(parseInt(e.target.value) || 0)}
                min={1}
                max={100}
                className="bg-secondary/50 border-border/50 mt-2"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGrantCreditsOpen(false)}>Cancel</Button>
            <Button
              onClick={handleGrantCredits}
              disabled={actionLoading === "credits"}
              className="bg-[#00F0FF] hover:bg-[#00F0FF]/90 text-[#0A0A0B]"
            >
              {actionLoading === "credits" ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Grant {creditsToGrant} Credits
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Subscription Dialog */}
      <Dialog open={assignSubOpen} onOpenChange={setAssignSubOpen}>
        <DialogContent className="bg-[#0A0A0B] border-border/50">
          <DialogHeader>
            <DialogTitle>Assign Subscription</DialogTitle>
            <DialogDescription>Manually assign a subscription plan to this user</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Plan</Label>
              <Select value={selectedPlan} onValueChange={setSelectedPlan}>
                <SelectTrigger className="bg-secondary/50 border-border/50 mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="starter">Starter</SelectItem>
                  <SelectItem value="pro">Pro</SelectItem>
                  <SelectItem value="enterprise">Enterprise</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Duration</Label>
              <Select value={selectedDuration} onValueChange={setSelectedDuration}>
                <SelectTrigger className="bg-secondary/50 border-border/50 mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="yearly">Yearly</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignSubOpen(false)}>Cancel</Button>
            <Button
              onClick={handleAssignSubscription}
              disabled={actionLoading === "subscription"}
              className="bg-[#00F0FF] hover:bg-[#00F0FF]/90 text-[#0A0A0B]"
            >
              {actionLoading === "subscription" ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Assign Subscription
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
